import { z } from "zod/v4";
import {
	bulkUpdateMembersResponseSchema,
	type bulkUpdateMembersSchema,
	clubMemberSchema,
	memberCountsSchema,
	paginatedClubMembersSchema,
	type updateClubMemberSchema,
	userSearchResultSchema,
} from "../schemas/club-member";
import { fetchApi, fetchVoid } from "./internal";

export type MemberListParams = {
	q?: string;
	is_trainer?: string;
	/** Membership role: owner | admin | trainer | member | organizer. */
	role?: string;
	/** Membership type: member | trainer | treasurer | … */
	member_type?: string;
	/**
	 * Membership status. A concrete status (e.g. "pending") filters to it; the
	 * literal "all" returns every status; omit to default to active members only.
	 */
	status?: string;
	limit?: number;
	offset?: number;
};

const MEMBER_LIST_PAGE_SIZE = 200;

function buildMemberListQuery(params?: MemberListParams): string {
	const searchParams = new URLSearchParams();
	if (params?.q) searchParams.set("q", params.q);
	if (params?.is_trainer) searchParams.set("is_trainer", params.is_trainer);
	if (params?.role) searchParams.set("role", params.role);
	if (params?.member_type) searchParams.set("member_type", params.member_type);
	if (params?.status) searchParams.set("status", params.status);
	if (params?.limit) searchParams.set("limit", String(params.limit));
	if (params?.offset) searchParams.set("offset", String(params.offset));
	const qs = searchParams.toString();
	return qs ? `?${qs}` : "";
}

export const members = {
	list: (clubId: string, params?: MemberListParams) =>
		fetchApi(`/clubs/${clubId}/members${buildMemberListQuery(params)}`, paginatedClubMembersSchema),
	counts: (clubId: string) => fetchApi(`/clubs/${clubId}/members/counts`, memberCountsSchema),
	/**
	 * Fetch every member matching `filters` by paging through the server, for
	 * flows that need the whole filtered set (CSV export, broadcast recipients)
	 * rather than the visible page.
	 */
	listAll: async (clubId: string, filters?: Omit<MemberListParams, "limit" | "offset">) => {
		const all: z.infer<typeof clubMemberSchema>[] = [];
		let offset = 0;
		for (;;) {
			const page = await members.list(clubId, {
				...filters,
				limit: MEMBER_LIST_PAGE_SIZE,
				offset,
			});
			all.push(...page.items);
			offset += page.items.length;
			if (page.items.length === 0 || all.length >= page.total) break;
		}
		return all;
	},
	searchUsers: (clubId: string, query: string) =>
		fetchApi(
			`/clubs/${clubId}/members/search?q=${encodeURIComponent(query)}`,
			z.array(userSearchResultSchema),
		),
	add: (
		clubId: string,
		data: {
			user_id?: string;
			email?: string;
			display_name?: string;
			role?: string;
			member_type?: string;
		},
	) =>
		fetchApi(`/clubs/${clubId}/members`, clubMemberSchema, {
			method: "POST",
			body: JSON.stringify(data),
		}),
	bulkImport: (clubId: string, members: { email: string; role: string }[]) =>
		fetchApi(
			`/clubs/${clubId}/members/import`,
			z.object({
				added: z.number(),
				skipped: z.number(),
				failed: z.number(),
				rows: z.array(
					z.object({
						email: z.string(),
						status: z.string(),
						message: z.string().optional(),
					}),
				),
			}),
			{ method: "POST", body: JSON.stringify({ members }) },
		),
	update: (clubId: string, userId: string, data: z.infer<typeof updateClubMemberSchema>) =>
		fetchVoid(`/clubs/${clubId}/members/${userId}`, {
			method: "PATCH",
			body: JSON.stringify(data),
		}),
	bulkUpdate: (clubId: string, data: z.infer<typeof bulkUpdateMembersSchema>) =>
		fetchApi(`/clubs/${clubId}/members`, bulkUpdateMembersResponseSchema, {
			method: "PATCH",
			body: JSON.stringify(data),
		}),
	remove: (clubId: string, userId: string) =>
		fetchVoid(`/clubs/${clubId}/members/${userId}`, { method: "DELETE" }),
};
